import { Copy, Pencil, UserPlus, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../api';

const ROLES = [
  { value: 'aprobador', label: 'Aprobador' },
  { value: 'solicitante', label: 'Solicitante' },
];

const INVITACION_INICIAL = { email: '', nombre: '', apellido: '', iniciales: '', rol: 'solicitante' };

export default function Equipo({ miRol }) {
  const esAdmin = miRol === 'admin';

  const [miembros, setMiembros] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [nuevaInvitacion, setNuevaInvitacion] = useState(INVITACION_INICIAL);
  const [invitando, setInvitando] = useState(false);
  const [ultimoEnlace, setUltimoEnlace] = useState(null);
  const [editando, setEditando] = useState(null); // { id, nombre, apellido, iniciales }
  const [guardandoEdicion, setGuardandoEdicion] = useState(false);

  function cargarMiembros() {
    setCargando(true);
    api.get('/equipo/').then((res) => setMiembros(res.data)).finally(() => setCargando(false));
  }

  useEffect(() => {
    cargarMiembros();
  }, []);

  async function handleInvitar(e) {
    e.preventDefault();
    setInvitando(true);
    try {
      const res = await api.post('/equipo/invitar/', nuevaInvitacion);
      setUltimoEnlace(res.data.enlace_invitacion);
      setNuevaInvitacion(INVITACION_INICIAL);
      cargarMiembros();
      toast.success('Invitación creada.');
    } catch (err) {
      const detalle = err.response?.data;
      const mensaje = detalle ? Object.values(detalle).flat().join(' ') : 'No se pudo invitar.';
      toast.error(mensaje);
    } finally {
      setInvitando(false);
    }
  }

  async function copiarEnlace() {
    await navigator.clipboard.writeText(ultimoEnlace);
    toast.success('Enlace copiado al portapapeles.');
  }

  async function cambiarRol(perfil, rol) {
    try {
      const res = await api.patch(`/equipo/${perfil.id}/`, { rol });
      setMiembros((prev) => prev.map((m) => (m.id === perfil.id ? res.data : m)));
    } catch {
      toast.error('No se pudo cambiar el rol.');
    }
  }

  async function alternarActivo(perfil) {
    try {
      const res = await api.patch(`/equipo/${perfil.id}/`, { activo: !perfil.activo });
      setMiembros((prev) => prev.map((m) => (m.id === perfil.id ? res.data : m)));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo actualizar.');
    }
  }

  function iniciarEdicion(m) {
    setEditando({ id: m.id, nombre: m.nombre, apellido: m.apellido, iniciales: m.iniciales });
  }

  async function guardarEdicion(e) {
    e.preventDefault();
    setGuardandoEdicion(true);
    try {
      const res = await api.patch(`/equipo/${editando.id}/`, {
        nombre: editando.nombre,
        apellido: editando.apellido,
        iniciales: editando.iniciales,
      });
      setMiembros((prev) => prev.map((m) => (m.id === editando.id ? res.data : m)));
      setEditando(null);
      toast.success('Datos actualizados.');
    } catch (err) {
      const detalle = err.response?.data;
      const mensaje = detalle ? Object.values(detalle).flat().join(' ') : 'No se pudo guardar.';
      toast.error(mensaje);
    } finally {
      setGuardandoEdicion(false);
    }
  }

  return (
    <div className="equipo">
      {esAdmin && (
        <div className="control-group formulario-invitar">
          <h4>Invitar a alguien de tu equipo</h4>
          <form onSubmit={handleInvitar}>
            <div className="coord-inputs">
              <div>
                <label>Nombre</label>
                <input
                  value={nuevaInvitacion.nombre}
                  onChange={(e) => setNuevaInvitacion((v) => ({ ...v, nombre: e.target.value }))}
                  placeholder="Nombre"
                  required
                />
              </div>
              <div>
                <label>Apellido</label>
                <input
                  value={nuevaInvitacion.apellido}
                  onChange={(e) => setNuevaInvitacion((v) => ({ ...v, apellido: e.target.value }))}
                  placeholder="Apellido"
                  required
                />
              </div>
            </div>

            <div className="coord-inputs">
              <div>
                <label>Iniciales</label>
                <input
                  value={nuevaInvitacion.iniciales}
                  onChange={(e) => setNuevaInvitacion((v) => ({ ...v, iniciales: e.target.value.toUpperCase() }))}
                  placeholder="Ej. JP"
                  maxLength={4}
                  required
                />
              </div>
              <div>
                <label>Rol</label>
                <select
                  value={nuevaInvitacion.rol}
                  onChange={(e) => setNuevaInvitacion((v) => ({ ...v, rol: e.target.value }))}
                >
                  {ROLES.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <label>Correo electrónico</label>
            <input
              type="email"
              value={nuevaInvitacion.email}
              onChange={(e) => setNuevaInvitacion((v) => ({ ...v, email: e.target.value }))}
              placeholder="compañero@empresa.com"
              required
            />

            <button className="btn-primary" type="submit" disabled={invitando}>
              <UserPlus size={16} /> {invitando ? 'Invitando…' : 'Invitar'}
            </button>
          </form>

          {ultimoEnlace && (
            <div className="enlace-invitacion">
              <p className="ayuda">
                Enlace de invitación (también se envió por correo, pero puedes compartirlo directo por si acaso):
              </p>
              <div className="enlace-copiar">
                <input readOnly value={ultimoEnlace} onFocus={(e) => e.target.select()} />
                <button type="button" className="btn-secundario" onClick={copiarEnlace}>
                  <Copy size={14} /> Copiar
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="control-group">
        <h4>Miembros del equipo</h4>
        {cargando ? (
          <p>Cargando…</p>
        ) : (
          <table className="tabla-historial">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Correo</th>
                <th>Iniciales</th>
                <th>Rol</th>
                <th>Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {miembros.map((m) => (
                <tr key={m.id}>
                  {editando?.id === m.id ? (
                    <td colSpan={6}>
                      <form className="fila-editar-miembro" onSubmit={guardarEdicion}>
                        <input
                          value={editando.nombre}
                          onChange={(e) => setEditando((v) => ({ ...v, nombre: e.target.value }))}
                          placeholder="Nombre"
                          required
                        />
                        <input
                          value={editando.apellido}
                          onChange={(e) => setEditando((v) => ({ ...v, apellido: e.target.value }))}
                          placeholder="Apellido"
                          required
                        />
                        <input
                          value={editando.iniciales}
                          onChange={(e) => setEditando((v) => ({ ...v, iniciales: e.target.value.toUpperCase() }))}
                          placeholder="Iniciales"
                          maxLength={4}
                        />
                        <button className="btn-primary" type="submit" disabled={guardandoEdicion}>
                          {guardandoEdicion ? 'Guardando…' : 'Guardar'}
                        </button>
                        <button type="button" className="btn-secundario" onClick={() => setEditando(null)}>
                          <X size={14} /> Cancelar
                        </button>
                      </form>
                    </td>
                  ) : (
                    <>
                      <td>{m.nombre_completo}</td>
                      <td>{m.email}</td>
                      <td>{m.iniciales_efectivas}</td>
                      <td>
                        {!esAdmin || m.rol === 'admin' ? (
                          m.rol === 'admin' ? 'Administrador' : ROLES.find((r) => r.value === m.rol)?.label
                        ) : (
                          <select value={m.rol} onChange={(e) => cambiarRol(m, e.target.value)}>
                            {ROLES.map((r) => (
                              <option key={r.value} value={r.value}>{r.label}</option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td>{m.activo ? 'Activo' : 'Inactivo'}</td>
                      <td>
                        <div className="acciones-tabla">
                          <button type="button" className="btn-secundario" onClick={() => iniciarEdicion(m)}>
                            <Pencil size={14} /> Editar
                          </button>
                          {esAdmin && m.rol !== 'admin' && (
                            <button
                              type="button"
                              className={`btn-secundario ${m.activo ? 'btn-peligro' : 'btn-aprobar'}`}
                              onClick={() => alternarActivo(m)}
                            >
                              {m.activo ? 'Desactivar' : 'Activar'}
                            </button>
                          )}
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
